import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { getRequest } from '../../shared/api/requests.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAuthStore } from '../auth/authStore'
import { STATUS_LABELS } from './components/requestsColDefs'
import type { RequestStatus } from '../../shared/api/types'
import { KPControlPanel } from './components/KPControlPanel'
import HistoryPanel from './components/HistoryPanel'

/**
 * Экран «История заявки» — drill-in из вкладки «Аналитика → КП» (для ADMIN/
 * Гендиректора и РОП). НЕ стандартная карточка заявки: только сводка вех
 * («Контроль КП») + полная лента истории (статусы + аудит), read-only.
 */

const ALLOWED = new Set(['ADMIN', 'GENERAL_DIRECTOR', 'ROP'])

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString('ru-RU') : '—'

const statusLabel = (s: string): string => STATUS_LABELS[s as RequestStatus] ?? s

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm text-gray-800">{value}</div>
    </div>
  )
}

export function RequestHistoryPage() {
  const { id } = useParams<{ id: string }>()
  const role = useAuthStore((s) => s.user?.role ?? '')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['requests', id, 'detail-history'],
    queryFn: () => getRequest(id!),
    enabled: !!id && ALLOWED.has(role),
  })

  if (!ALLOWED.has(role)) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">История заявки</h1>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          Экран доступен только администратору и руководителям отделов.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить заявку" />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Link to="/analytics" className="text-sm text-brand-600 hover:text-brand-700">
            ← Аналитика
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">
            История заявки {data.requestNumber ?? ''}
          </h1>
        </div>
        <Link
          to={`/requests/${data.id}`}
          className="text-sm text-gray-500 hover:text-gray-800 underline decoration-dotted"
        >
          Открыть карточку заявки
        </Link>
      </div>

      {/* Шапка-сводка */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetaItem label="Клиент" value={data.client?.name ?? '—'} />
          <MetaItem label="Менеджер" value={data.manager?.name ?? '—'} />
          <MetaItem label="Статус" value={statusLabel(data.status)} />
          <MetaItem label="Создана" value={fmtDate(data.createdAt)} />
          <MetaItem label="КП отправлено" value={fmtDate(data.quoteSentAt)} />
          <MetaItem label="Дедлайн" value={fmtDate(data.deadline)} />
        </div>
      </div>

      {/* Таймлайн «Контроль КП» (read-only) */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Контроль КП</h2>
        <KPControlPanel
          requestId={data.id}
          status={data.status}
          quoteSentAt={data.quoteSentAt}
          canEdit={false}
        />
      </div>

      {/* Полная лента истории (статусы + аудит) */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">История изменений</h2>
        <HistoryPanel requestId={data.id} />
      </div>
    </div>
  )
}
